import React, { useState } from 'react';

const STEPS = ['Personal Info', 'Account Details', 'Preferences', 'Review & Submit'];

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '',
  username: '', password: '', confirmPassword: '', role: 'Viewer',
  theme: 'Light', notifications: true, newsletter: false, language: 'English',
};

const ROLES = ['Viewer', 'Editor', 'Admin', 'Manager'];
const LANGUAGES = ['English', 'French', 'German', 'Spanish', 'Japanese'];

export default function MultiStepForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setData(d => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: '' }));
  };

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      if (!data.firstName.trim()) errs.firstName = 'Required';
      if (!data.lastName.trim()) errs.lastName = 'Required';
      if (!data.email.trim()) errs.email = 'Required';
      else if (!/\S+@\S+\.\S+/.test(data.email)) errs.email = 'Invalid email';
      if (!data.phone.trim()) errs.phone = 'Required';
    }
    if (step === 1) {
      if (!data.username.trim()) errs.username = 'Required';
      else if (data.username.length < 4) errs.username = 'Min 4 characters';
      if (!data.password) errs.password = 'Required';
      else if (data.password.length < 6) errs.password = 'Min 6 characters';
      if (data.password !== data.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    }
    return errs;
  };

  const next = () => {
    const errs = validateStep();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
  };

  const back = () => setStep(s => s - 1);

  const submit = () => setSubmitted(true);
  const reset = () => { setData(EMPTY); setStep(0); setSubmitted(false); setErrors({}); };

  const Field = ({ label, name, type = 'text', placeholder }) => (
    <div className="msf-field">
      <label>{label}</label>
      <input name={name} type={type} value={data[name]} onChange={change}
        className={errors[name] ? 'input-err' : ''} placeholder={placeholder} />
      {errors[name] && <span className="eval-err">{errors[name]}</span>}
    </div>
  );

  if (submitted) return (
    <div className="eval-section">
      <h2 className="eval-section-title">📝 Multi-Step Form <span className="eval-badge-scenario">Wizard / Validation</span></h2>
      <div className="msf-success">
        <div className="msf-success-icon">✅</div>
        <h3>Registration Submitted!</h3>
        <div className="msf-review-grid">
          <span>Name:</span><span>{data.firstName} {data.lastName}</span>
          <span>Email:</span><span>{data.email}</span>
          <span>Username:</span><span>{data.username}</span>
          <span>Role:</span><span>{data.role}</span>
          <span>Language:</span><span>{data.language}</span>
          <span>Theme:</span><span>{data.theme}</span>
          <span>Notifications:</span><span>{data.notifications ? 'On' : 'Off'}</span>
          <span>Newsletter:</span><span>{data.newsletter ? 'Yes' : 'No'}</span>
        </div>
        <button className="eval-btn eval-btn-primary" onClick={reset} style={{marginTop:'16px'}}>Start Over</button>
      </div>
    </div>
  );

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">📝 Multi-Step Form <span className="eval-badge-scenario">Wizard / Validation</span></h2>
      <p className="eval-desc">Test: wizard navigation, per-step validation, conditional fields, final review before submit.</p>

      <div className="msf-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`msf-step ${i === step ? 'msf-step-active' : ''} ${i < step ? 'msf-step-done' : ''}`}>
            <div className="msf-step-num">{i < step ? '✓' : i + 1}</div>
            <div className="msf-step-label">{s}</div>
          </div>
        ))}
      </div>

      <div className="msf-body">
        {step === 0 && (
          <div className="msf-grid">
            <Field label="First Name *" name="firstName" placeholder="John" />
            <Field label="Last Name *" name="lastName" placeholder="Doe" />
            <Field label="Email *" name="email" type="email" placeholder="john@example.com" />
            <Field label="Phone *" name="phone" placeholder="+1 555 0000" />
          </div>
        )}
        {step === 1 && (
          <div className="msf-grid">
            <Field label="Username *" name="username" placeholder="johndoe" />
            <div className="msf-field">
              <label>Role</label>
              <select name="role" value={data.role} onChange={change}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <Field label="Password *" name="password" type="password" placeholder="••••••" />
            <Field label="Confirm Password *" name="confirmPassword" type="password" placeholder="••••••" />
          </div>
        )}
        {step === 2 && (
          <div className="msf-prefs">
            <div className="msf-field">
              <label>Theme</label>
              <div className="msf-radio-group">
                {['Light', 'Dark', 'System'].map(t => (
                  <label key={t} className="msf-radio">
                    <input type="radio" name="theme" value={t} checked={data.theme === t} onChange={change} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
            <div className="msf-field">
              <label>Language</label>
              <select name="language" value={data.language} onChange={change}>
                {LANGUAGES.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div className="msf-field">
              <label className="msf-chk"><input type="checkbox" name="notifications" checked={data.notifications} onChange={change} /> Enable Notifications</label>
            </div>
            <div className="msf-field">
              <label className="msf-chk"><input type="checkbox" name="newsletter" checked={data.newsletter} onChange={change} /> Subscribe to Newsletter</label>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="msf-review">
            <h4>Review your information</h4>
            <div className="msf-review-grid">
              <span>Name:</span><span>{data.firstName} {data.lastName}</span>
              <span>Email:</span><span>{data.email}</span>
              <span>Phone:</span><span>{data.phone}</span>
              <span>Username:</span><span>{data.username}</span>
              <span>Role:</span><span>{data.role}</span>
              <span>Theme:</span><span>{data.theme}</span>
              <span>Language:</span><span>{data.language}</span>
              <span>Notifications:</span><span>{data.notifications ? 'On' : 'Off'}</span>
              <span>Newsletter:</span><span>{data.newsletter ? 'Yes' : 'No'}</span>
            </div>
          </div>
        )}
      </div>

      <div className="msf-nav">
        {step > 0 && <button className="eval-btn" onClick={back}>← Back</button>}
        <div style={{ flex: 1 }} />
        {step < STEPS.length - 1
          ? <button className="eval-btn eval-btn-primary" onClick={next}>Next →</button>
          : <button className="eval-btn eval-btn-success" onClick={submit}>Submit Registration</button>
        }
      </div>
    </div>
  );
}
